import { Link, useRouter } from 'found';
import { find } from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import RSLogoLight from 'assets/RS_logo-white.png';
import RSLogo from 'assets/RS_logo.png';
import Badge from 'components/Badge';
import { Col, Row } from 'components/Grid';
import HTMLFragment from 'components/HTMLFragment';
import { Paper, useTheme } from '@mui/material';

import AppBar from './AppBar';

const useStyles = makeStyles(theme => ({
  inkBar: {
    backgroundColor: theme.palette.text.primary,
    marginTop: -5,
    height: 5,
  },
  tabs: {},
  col: {
    flex: 1,
  },
  container: {
    position: 'fixed',
    width: '100vw',
    top: 0,
    zIndex: 1100,
    backgroundColor: theme.palette.background.paper,
  },
  iconButton: {
    padding: 0,
    width: null,
  },
  leftOrRight: { minWidth: 24 },
  left: {
    justifyContent: 'flex-start',
  },
  logo: {
    marginLeft: 20,
    height: 42,
    width: 'auto',
  },
  middle: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  middleSmall: { fontSize: 13 },
  right: {
    justifyContent: 'flex-end',
  },
  tab: {
    padding: '0 10px',
    minWidth: 100,
    textTransform: 'none',
    position: 'relative',
    borderRadius: 0,
    marginBottom: 5,
  },
  tabLabel: {
    fontWeight: '700',
  },
  blueBar: {},
  inner: {
    flex: 1,
    justifyContent: 'space-between',
  },
  greenBar: {
    background: theme.palette.background.default,
  },
}));

const TopNav = ({
  appBar = true,
  backLink,
  hasRightDrawer,
  items,
  links,
  mobile,
  openLeftDrawer,
  openRightDrawer,
  pathname,
  rightButton,
  title,
  titleLink,
}) => {
  const theme = useTheme();
  const { router } = useRouter();
  const classes = useStyles();
  const active = useMemo(
    () => find(
        items,
        item => item.path && item.path.split('/')[1] === pathname.split('/')[1],
      ) || null,
    [items, pathname],
  );
  const mobileTitle = useMemo(() => {
    return backLink ? `${backLink.label} ${title}` : title;
  }, [backLink, title]);

  return (
    <Paper className={classes.container}>
      <Col className={classes.col}>
        <Row centered className={classes.blueBar}>
          <Row inner className={classes.inner}>
            <Row centered left className={classes.leftOrRight}>
              <IconButton
                onClick={openLeftDrawer}
                className={classes.iconButton}
                size='large'
              >
                <MenuIcon />
              </IconButton>
              {!mobile && (
                <Link to='/'>
                  <img
                    style={{ height: 42 }}
                    src={theme.palette.mode === 'dark' ? RSLogoLight : RSLogo}
                    className={classes.logo}
                    alt='logo'
                  />
                </Link>
              )}
            </Row>
            {mobile && (
              <Row
                inner
                centered
                className={[
                  classes.middle,
                  mobileTitle?.length > 20 ? classes.middleSmall : '',
                ]}
              >
                <HTMLFragment>{mobileTitle}</HTMLFragment>
              </Row>
            )}
            <Row centered right className={classes.leftOrRight}>
              {useMemo(
                () => (mobile ? (
                    rightButton
                    || (hasRightDrawer ? (
                      <IconButton
                        onClick={openRightDrawer}
                        className={classes.iconButton}
                        size='large'
                      >
                        <MoreVertIcon />
                      </IconButton>
                    ) : (
                      ' '
                    ))
                  ) : (
                    <Tabs
                      classes={{
                        indicator: classes.inkBar,
                        root: classes.tabs,
                      }}
                      onChange={(event, key) => {
                        const currentItem = find(
                          items,
                          item => item.key === key,
                        );
                        if (currentItem.handler) {
                          currentItem.handler();
                        }
                        router.push(currentItem.path || pathname);
                      }}
                      value={active && active.key}
                    >
                      {items.map(item => (
                        <Tab
                          containerElement={<Link to={item.path || pathname} />}
                          icon={item.icon}
                          key={item.key}
                          label={(
                            <Badge count={item.badge}>
                              <span className={classes.tabLabel}>
                                {item.label}
                              </span>
                            </Badge>
                          )}
                          className={classes.tab}
                          value={item.key}
                        />
                      ))}
                    </Tabs>
                  )),
                [
                  mobile,
                  rightButton,
                  hasRightDrawer,
                  openRightDrawer,
                  classes.iconButton,
                  classes.inkBar,
                  classes.tabs,
                  classes.tabLabel,
                  classes.tab,
                  active,
                  items,
                  router,
                  pathname,
                ],
              )}
              {/* <NewVersion /> */}
            </Row>
          </Row>
        </Row>
        {!mobile && appBar && (
          <Row centered className={classes.greenBar}>
            <Row inner>
              <AppBar
                backLink={backLink}
                links={links}
                title={title}
                titleLink={titleLink}
              />
            </Row>
          </Row>
        )}
      </Col>
    </Paper>
  );
};

TopNav.propTypes = {
  appBar: PropTypes.bool,
  backLink: PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
  }),
  hasRightDrawer: PropTypes.bool,
  items: PropTypes.array.isRequired,
  links: PropTypes.array,
  mobile: PropTypes.bool,
  openLeftDrawer: PropTypes.func.isRequired,
  openRightDrawer: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  rightButton: PropTypes.node,
  title: PropTypes.string.isRequired,
  titleLink: PropTypes.string,
};

export default TopNav;
